import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
} from '@wix/tpa-settings';
import {
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import DefaultDesign from '../../design';
import { PresetSettings } from '../../types/designTypes';
import { ItemAppearance } from './settingsParams';
import { ItemAppearanceStyle } from '../../enums';

const defaultDesign = DefaultDesign;

export const convertItemAppearanceStyleToItemAppearance = (
  itemAppearanceStyle: ItemAppearanceStyle | ItemAppearance,
) => {
  if (typeof itemAppearanceStyle === 'number') {
    switch (itemAppearanceStyle) {
      case ItemAppearanceStyle.BOX:
        return ItemAppearance.BOX;
      case ItemAppearanceStyle.LINE:
        return ItemAppearance.LINE;
      case ItemAppearanceStyle.NONE:
        return ItemAppearance.NONE;
    }
    return itemAppearanceStyle as ItemAppearance;
  } else if (typeof itemAppearanceStyle === 'string') {
    switch (itemAppearanceStyle) {
      case ItemAppearance.BOX:
        return ItemAppearanceStyle.BOX;
      case ItemAppearance.LINE:
        return ItemAppearanceStyle.LINE;
      case ItemAppearance.NONE:
        return ItemAppearanceStyle.NONE;
    }
    return itemAppearanceStyle as ItemAppearanceStyle;
  }
  return itemAppearanceStyle;
};
export type IFaqOoiStylesParams = {
  headerTextColor: StyleParamType.Color;
  headerTextFont: StyleParamType.Font;
  selectedCategoryColor: StyleParamType.Color;
  categoryOtherTextColor: StyleParamType.Color;
  categoryOtherTextFont: StyleParamType.Font;
  widgetColor: StyleParamType.Color;
  accordionCardBgColor: StyleParamType.Color;
  questionTextColor: StyleParamType.Color;
  questionTextFont: StyleParamType.Font;
  questionStripesTextColor: StyleParamType.Color;
  questionStripesTextFont: StyleParamType.Font;
  questionNumberTextColor: StyleParamType.Color;
  questionNumberTextFont: StyleParamType.Font;
  questionNumberHoverColor: StyleParamType.Color;
  questionNumberBgColor: StyleParamType.Color;
  answerTextColor: StyleParamType.Color;
  answerTextFont: StyleParamType.Font;
  answersLinksColor: StyleParamType.Color;
  dividerCollapseColor: StyleParamType.Color;
  dividerCollapseWidth: StyleParamType.Number;
  numberQuestionsLayoutBorderColor: StyleParamType.Color;
  numberQuestionsLayoutBorderWidth: StyleParamType.Number;
  numberAnswersLayoutBorderColor: StyleParamType.Color;
  numberAnswersLayoutBorderWidth: StyleParamType.Number;
  numberAnswersLayoutBgColor: StyleParamType.Color;
  showFAQCategories: StyleParamType.Boolean; // DefaultValue: true;,
  showTitle: StyleParamType.Boolean; // DefaultValue: true;,
  showSearchBar: StyleParamType.Boolean; // DefaultValue: true;,
  showSocialIcons: StyleParamType.Boolean; // DefaultValue: true;,
  showFacebookIcon: StyleParamType.Boolean; // DefaultValue: true;,
  showTwitterIcon: StyleParamType.Boolean; // DefaultValue: true;,
  showLinkedinIcon: StyleParamType.Boolean; // DefaultValue: true;,
  showCopyQuestionLink: StyleParamType.Boolean; // DefaultValue: true;,
  presetSettings: StyleParamType.Number; // DefaultValue: 0; OPTIONS=[ACCORDION_TOP_CATEGORIES=0,ACCORDION_SIDE_CATEGORIES=1,NUMERIC_TOP_CATEGORIES=2,NUMERIC_SIDE_CATEGORIES=3,LIST_TOP_CATEGORIES=4,LIST_SIDE_CATEGORIES=5,LINES_TOP_CATEGORIES=6,LINES_SIDE_CATEGORIES=7]; This setting determines the overall layout and category positioning of the FAQ App, allowing you to choose from various preset designs such as accordion, numeric, list, and lines layouts with categories displayed either at the top or on the side.;,
  itemAppearanceStyle: StyleParamType.Number; // DefaultValue: 0; OPTIONS=[BOX=0,LINE=1,NONE=2]; This setting determines the visual style of each FAQ item, allowing you to choose between a boxed layout, a lined layout, or no special styling.;,
  /**
 *           gap: 12,
          betweencols: 12,
          vertical: 32,
          horizontal: 32,
*/
  spacingGap: StyleParamType.Number;
  spacingVertical: StyleParamType.Number;
  spacingHorizontal: StyleParamType.Number;
  isEditor3: StyleParamType.Boolean;
};

export const stylesParams = createStylesParams<IFaqOoiStylesParams>({
  spacingGap: {
    type: StyleParamType.Number,
    key: 'spacingGap',
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 12,
      },
      ai: {
        description:
          'DefaultValue:12; This setting determines the gap between the FAQ items, allowing you to adjust the spacing between the FAQ items.',
      },
    },
  },

  spacingVertical: {
    type: StyleParamType.Number,
    key: 'spacingVertical',
    getDefaultValue: () => 32,
    metadata: {
      type: StyleType.paddingTop,
      defaultValue: {
        type: 'static',
        value: 32,
      },
      ai: {
        description:
          'DefaultValue:32; This setting determines the top and bottom padding of FAQ app within its container',
      },
    },
  },
  spacingHorizontal: {
    type: StyleParamType.Number,
    key: 'spacingHorizontal',
    getDefaultValue: () => 32,
    metadata: {
      type: StyleType.paddingLeft,
      defaultValue: {
        type: 'static',
        value: 32,
      },
      ai: {
        description:
          'DefaultValue:32; This setting determines the left and right padding of FAQ app within its container',
      },
    },
  },
  headerTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.headerText.color.name,
    getDefaultValue: wixColorParam(defaultDesign.headerText.color.value),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(defaultDesign.headerText.color.editor3Value),
      ai: {
        description: 'determines the color of the FAQ App title text.',
      },
    },
  },

  headerTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.headerText.font.name,
    inheritDesktop: true,
    getDefaultValue: ({ isMobile, textPresets }) =>
      wixFontParam(defaultDesign.headerText.font.value, {
        size: isMobile
          ? defaultDesign.headerText.font.startWithMobileSize
          : defaultDesign.headerText.font.startWithSize,
        htmlTag: defaultDesign.headerText.font.htmlTag,
      })({ textPresets }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.headerText.font.editor3Value),
        size: defaultDesign.headerText.font.startWithSize,
      },
      mobileDefaultValue: {
        ...wixFont(defaultDesign.headerText.font.editor3Value),
        size: defaultDesign.headerText.font.startWithMobileSize,
      },
      ai: {
        description: 'determines the font of the FAQ App title text.',
      },
    },
  },
  selectedCategoryColor: {
    type: StyleParamType.Color,
    key: defaultDesign.selectedCategory.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.selectedCategory.color.value,
      defaultDesign.selectedCategory.color.opacity || 1,
    ),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        ...wixColor(defaultDesign.selectedCategory.color.editor3Value),
        opacity: defaultDesign.selectedCategory.color.opacity || 1,
      },
      ai: {
        description: 'determines the color of the selected category text.',
      },
    },
  },
  categoryOtherTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.categoryOtherText.color.name,
    getDefaultValue: wixColorParam(defaultDesign.categoryOtherText.color.value),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(
        defaultDesign.categoryOtherText.color.editor3Value,
      ),
      ai: {
        description: 'determines the color of the other category text.',
      },
    },
  },
  categoryOtherTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.categoryOtherText.font.name,
    getDefaultValue: wixFontParam(defaultDesign.categoryOtherText.font.value, {
      size: defaultDesign.categoryOtherText.font.startWithSize,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.categoryOtherText.font.editor3Value),
        size: defaultDesign.categoryOtherText.font.startWithSize,
      },
      ai: {
        description: 'determines the font of the other category text.',
      },
    },
  },
  widgetColor: {
    type: StyleParamType.Color,
    key: defaultDesign.widget.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.widget.color.value,
      defaultDesign.widget.color.opacity || 1,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(defaultDesign.widget.color.editor3Value),
      ai: {
        description: 'determines the color of the FAQ app background.',
      },
    },
  },
  accordionCardBgColor: {
    type: StyleParamType.Color,
    key: defaultDesign.accordionCardBgColor.name,
    getDefaultValue: wixColorParam(
      defaultDesign.accordionCardBgColor.value,
      defaultDesign.accordionCardBgColor.opacity || 1,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        ...wixColor(defaultDesign.accordionCardBgColor.editor3Value),
        opacity: defaultDesign.accordionCardBgColor.opacity || 1,
      },
      ai: {
        description: 'determines the background color of accordion FAQ cards.',
      },
    },
  },
  questionTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.questionText.color.name,
    getDefaultValue: wixColorParam(defaultDesign.questionText.color.value),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(defaultDesign.questionText.color.editor3Value),
      ai: {
        description: 'determines the color of the FAQ question text.',
      },
    },
  },
  questionTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.questionText.font.name,
    // Intentionally no default `htmlTag` here: leaving it undefined lets the
    // widget derive the tag from the current font preset via
    // useQuestionHtmlTag, so changing the preset in the font picker updates
    // the rendered heading tag. A user-picked SEO tag persists onto the font
    // via the picker and overrides the derivation.
    getDefaultValue: ({ isMobile, textPresets }) =>
      wixFontParam(defaultDesign.questionText.font.value, {
        lineHeight: defaultDesign.questionText.font.lineHeight,
        size: isMobile
          ? defaultDesign.questionText.font.startWithMobileSize
          : defaultDesign.questionText.font.startWithSize,
      })({ textPresets }),

    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.questionText.font.editor3Value),
        size: defaultDesign.questionText.font.startWithSize,
      },
      mobileDefaultValue: {
        ...wixFont(defaultDesign.questionText.font.editor3Value),
        size: defaultDesign.questionText.font.startWithMobileSize,
      },
      ai: {
        description: 'determines the font of the FAQ question text.',
      },
    },
  },
  questionStripesTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.questionStripesText.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.questionStripesText.color.value,
    ),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(
        defaultDesign.questionStripesText.color.editor3Value,
      ),
      ai: {
        description:
          'This setting determines the color of the FAQ question stripes text.',
      },
    },
  },
  questionStripesTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.questionStripesText.font.name,
    getDefaultValue: wixFontParam(
      defaultDesign.questionStripesText.font.value,
      { size: defaultDesign.questionStripesText.font.startWithSize },
    ),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.questionStripesText.font.editor3Value),
        size: defaultDesign.questionStripesText.font.startWithSize,
      },
      ai: {
        description: 'determines the font of the FAQ question number text.',
      },
    },
  },
  questionNumberTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.questionNumberText.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.questionNumberText.color.value,
    ),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(
        defaultDesign.questionNumberText.color.editor3Value,
      ),
      ai: {
        description: 'determines the color of the FAQ question number text.',
      },
    },
  },
  questionNumberTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.questionNumberText.font.name,
    getDefaultValue: ({ isMobile, textPresets }) =>
      wixFontParam(defaultDesign.questionNumberText.font.value, {
        size: isMobile
          ? defaultDesign.questionNumberText.font.startWithMobileSize
          : defaultDesign.questionNumberText.font.startWithSize,
      })({ textPresets }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.questionNumberText.font.editor3Value),
        size: defaultDesign.questionNumberText.font.startWithSize,
      },
      mobileDefaultValue: {
        ...wixFont(defaultDesign.questionNumberText.font.editor3Value),
        size: defaultDesign.questionNumberText.font.startWithMobileSize,
      },
      ai: {
        description: 'determines the font of the FAQ question number text.',
      },
    },
  },
  questionNumberHoverColor: {
    type: StyleParamType.Color,
    key: defaultDesign.questionNumberHover.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.questionNumberHover.color.value,
      defaultDesign.questionNumberHover.color.opacity || 1,
    ),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        ...wixColor(defaultDesign.questionNumberHover.color.editor3Value),
        opacity: defaultDesign.questionNumberHover.color.opacity || 1,
      },
      ai: {
        description:
          'determines the color of the FAQ question number hover text.',
      },
    },
  },
  questionNumberBgColor: {
    type: StyleParamType.Color,
    key: defaultDesign.questionNumberBgColor.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.questionNumberBgColor.color.value,
      defaultDesign.questionNumberBgColor.color.opacity || 1,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        ...wixColor(defaultDesign.questionNumberBgColor.color.editor3Value),
        opacity: defaultDesign.questionNumberBgColor.color.opacity || 1,
      },
      ai: {
        description:
          'determines the color of the FAQ question number background text.',
      },
    },
  },
  numberQuestionsLayoutBorderColor: {
    type: StyleParamType.Color,
    key: defaultDesign.numberQuestionsLayoutBorder.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.numberQuestionsLayoutBorder.color.value,
      defaultDesign.numberQuestionsLayoutBorder.color.opacity || 0.75,
    ),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(
        defaultDesign.numberQuestionsLayoutBorder.color.editor3Value,
      ),
      ai: {
        description:
          'determines the color of the FAQ question number border text.',
      },
    },
  },

  numberQuestionsLayoutBorderWidth: {
    type: StyleParamType.Number,
    key: defaultDesign.numberQuestionsLayoutBorder.border.name,
    getDefaultValue: () =>
      defaultDesign.numberQuestionsLayoutBorder.border.value,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: defaultDesign.numberQuestionsLayoutBorder.border.value,
      },
      ai: {
        description:
          'determines the width of the FAQ question number border text.',
      },
    },
  },
  numberAnswersLayoutBgColor: {
    type: StyleParamType.Color,
    key: defaultDesign.numberAnswersLayoutBgColor.name,
    getDefaultValue: wixColorParam(
      defaultDesign.numberAnswersLayoutBgColor.value,
      defaultDesign.numberAnswersLayoutBgColor.opacity,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        ...wixColor(defaultDesign.numberAnswersLayoutBgColor.editor3Value),
        opacity: defaultDesign.numberAnswersLayoutBgColor.opacity || 1,
      },
      ai: {
        description:
          'determines the color of the FAQ question number background text.',
      },
    },
  },
  numberAnswersLayoutBorderColor: {
    type: StyleParamType.Color,
    key: defaultDesign.numberAnswersLayoutBorder.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.numberAnswersLayoutBorder.color.value,
      defaultDesign.numberAnswersLayoutBorder.color.opacity || 0.75,
    ),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        ...wixColor(defaultDesign.numberAnswersLayoutBorder.color.editor3Value),
        opacity: defaultDesign.numberAnswersLayoutBorder.color.opacity || 0.75,
      },
      ai: {
        description:
          'determines the color of the FAQ question number border text.',
      },
    },
  },
  numberAnswersLayoutBorderWidth: {
    type: StyleParamType.Number,
    key: defaultDesign.numberAnswersLayoutBorder.border.name,
    getDefaultValue: () => defaultDesign.numberAnswersLayoutBorder.border.value,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: defaultDesign.numberAnswersLayoutBorder.border.value,
      },
      ai: {
        description:
          'determines the width of the FAQ question number border text.',
      },
    },
  },
  answerTextColor: {
    type: StyleParamType.Color,
    key: defaultDesign.answerText.color.name,
    getDefaultValue: wixColorParam(defaultDesign.answerText.color.value),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(defaultDesign.answerText.color.editor3Value),
      ai: {
        description: 'determines the color of the FAQ answer text.',
      },
    },
  },
  answerTextFont: {
    type: StyleParamType.Font,
    key: defaultDesign.answerText.font.name,
    getDefaultValue: wixFontParam(defaultDesign.answerText.font.value, {
      size: defaultDesign.answerText.font.startWithSize,
      lineHeight: defaultDesign.answerText.font.lineHeight,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        ...wixFont(defaultDesign.answerText.font.editor3Value),
        size: defaultDesign.answerText.font.startWithSize,
        // lineHeight: defaultDesign.answerText.font.lineHeight,
      },
      ai: {
        description: 'determines the font of the FAQ answer text.',
      },
    },
  },
  answersLinksColor: {
    type: StyleParamType.Color,
    key: defaultDesign.answersLinks.color.name,
    getDefaultValue: wixColorParam(defaultDesign.answersLinks.color.value),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(defaultDesign.answersLinks.color.editor3Value),
      ai: {
        description: 'determines the color of the FAQ answer links text.',
      },
    },
  },
  dividerCollapseColor: {
    type: StyleParamType.Color,
    key: defaultDesign.dividerCollapse.color.name,
    getDefaultValue: wixColorParam(
      defaultDesign.dividerCollapse.color.value,
      defaultDesign.dividerCollapse.color.opacity || 1,
    ),

    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        ...wixColor(defaultDesign.dividerCollapse.color.editor3Value),
        opacity: defaultDesign.dividerCollapse.color.opacity || 1,
      },
      ai: {
        description: 'determines the color of the FAQ divider collapse text.',
      },
    },
  },
  dividerCollapseWidth: {
    type: StyleParamType.Number,
    key: defaultDesign.dividerCollapse.border.name,
    getDefaultValue: () => defaultDesign.dividerCollapse.border.value,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: defaultDesign.dividerCollapse.border.value,
      },
      ai: {
        description: 'determines the width of the FAQ divider collapse text.',
      },
    },
  },
  showTitle: {
    type: StyleParamType.Boolean,
    key: 'showTitle',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the FAQ App title is displayed at the top of the FAQ component.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showSearchBar: {
    type: StyleParamType.Boolean,
    key: 'showSearchBar',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the search bar is displayed in the FAQ App, allowing users to search for specific questions and answers.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showSocialIcons: {
    type: StyleParamType.Boolean,
    key: 'showSocialIcons',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether social media icons are displayed in the FAQ App, allowing users to share FAQ content on various social platforms.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showFacebookIcon: {
    type: StyleParamType.Boolean,
    key: 'showFacebookIcon',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the Facebook social icon is displayed in the FAQ App, allowing users to share FAQ content on Facebook.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showTwitterIcon: {
    type: StyleParamType.Boolean,
    key: 'showTwitterIcon',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the Twitter social icon is displayed in the FAQ App, allowing users to share FAQ content on Twitter.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showLinkedinIcon: {
    type: StyleParamType.Boolean,
    key: 'showLinkedinIcon',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the LinkedIn social icon is displayed in the FAQ App, allowing users to share FAQ content on LinkedIn.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showCopyQuestionLink: {
    type: StyleParamType.Boolean,
    key: 'showCopyQuestionLink',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether a "Copy Question Link" icon is displayed alongside each FAQ question, allowing users to easily copy a direct link to the specific question for sharing or reference purposes.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  showFAQCategories: {
    type: StyleParamType.Boolean,
    key: 'showFAQCategories',
    getDefaultValue: () => true,
    metadata: {
      ai: {
        description:
          'DefaultValue:true;[OPTIONS=true,false]; This setting determines whether the FAQ categories are displayed in the FAQ App, allowing users to easily navigate through different topics.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },

  presetSettings: {
    type: StyleParamType.Number,
    key: 'presetSettings',
    getDefaultValue: () => PresetSettings.ACCORDION_TOP_CATEGORIES,
    metadata: {
      ai: {
        description:
          'DefaultValue:0; OPTIONS=[ACCORDION_TOP_CATEGORIES=0,ACCORDION_SIDE_CATEGORIES=1,NUMERIC_TOP_CATEGORIES=2,NUMERIC_SIDE_CATEGORIES=3,LIST_TOP_CATEGORIES=4,LIST_SIDE_CATEGORIES=5,LINES_TOP_CATEGORIES=6,LINES_SIDE_CATEGORIES=7]; This setting determines the overall layout and category positioning of the FAQ App, allowing you to choose from various preset designs such as accordion, numeric, list, and lines layouts with categories displayed either at the top or on the side.',
      },
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: PresetSettings.ACCORDION_TOP_CATEGORIES,
      },
    },
  },
  itemAppearanceStyle: {
    type: StyleParamType.Number,
    key: 'itemAppearanceStyle',
    getDefaultValue: () => ItemAppearanceStyle.BOX,
    metadata: {
      ai: {
        description:
          'DefaultValue:0; OPTIONS=[BOX=0,LINE=1,NONE=2]; This setting determines the visual style of each FAQ item, allowing you to choose between a boxed layout, a lined layout, or no special styling.',
      },
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: ItemAppearanceStyle.BOX,
      },
    },
  },
  isEditor3: {
    type: StyleParamType.Boolean,
    key: 'isEditor3',
    getDefaultValue: () => false,
    metadata: {
      ai: {
        description:
          'DefaultValue:false;[OPTIONS=true,false]; This setting determines whether the FAQ App is in editor3 mode.',
      },
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
});

export default stylesParams;
